import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/api";
import { logout } from "./authSlice";
import { API_ENDPOINTS } from "../config/constants";
import { getReadableApiError } from "../utils/readableApiResponse";

const createRecentActivityItem = ({ type, label, query = "" }) => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type,
    label,
    query,
    createdAt: new Date().toISOString(),
});

const getCreatedAtTime = (createdAt) => {
    if (!createdAt) return 0;

    const time = new Date(createdAt).getTime();

    return Number.isNaN(time) ? 0 : time;
};

const getBackendSearchStatus = (search) =>
    typeof search?.status === "string" ? search.status.trim() : "";

const normalizeLeadSearchStatus = (status) => {
    const normalizedStatus = String(status || "")
        .trim()
        .toLowerCase();

    if (
        normalizedStatus === "processing" ||
        normalizedStatus === "external_search_in_progress" ||
        normalizedStatus === "superseded"
    ) {
        return "processing";
    }

    if (
        normalizedStatus === "done" ||
        normalizedStatus === "completed" ||
        normalizedStatus === "success"
    ) {
        return "completed";
    }

    if (
        normalizedStatus === "failed" ||
        normalizedStatus === "error" ||
        normalizedStatus === "system_error" ||
        normalizedStatus === "cancelled"
    ) {
        return "failed";
    }

    if (normalizedStatus === "no_results") {
        return "no_results";
    }

    return status || null;
};

const getLeadResultsFromResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.leads)) return data.leads;
    if (Array.isArray(data?.data)) return data.data;

    return [];
};

// Removes deleted searches and exact duplicate API records.
const normalizeSearchHistory = (searches) => {
    if (!Array.isArray(searches)) return [];

    const uniqueSearches = new Map();

    searches.forEach((search, index) => {
        const queryString = search?.query_string?.trim();

        if (!queryString || search?.is_deleted === true) {
            return;
        }

        const normalizedSearch = {
            ...search,
            query_string: queryString,
            backend_status: getBackendSearchStatus(search),
        };
        const searchId =
            search?.search_id ||
            search?.id ||
            `${queryString}-${search?.created_at || index}`;
        const existingSearch = uniqueSearches.get(searchId);

        const isNewerSearch =
            getCreatedAtTime(normalizedSearch.created_at) >
            getCreatedAtTime(existingSearch?.created_at);
        const hasBackendStatus =
            normalizedSearch.backend_status &&
            !existingSearch?.backend_status;

        if (!existingSearch || isNewerSearch || hasBackendStatus) {
            uniqueSearches.set(searchId, normalizedSearch);
        }
    });

    return Array.from(uniqueSearches.values()).sort(
        (firstSearch, secondSearch) =>
            getCreatedAtTime(secondSearch.created_at) -
            getCreatedAtTime(firstSearch.created_at),
    );
};

const normalizeSearchResponse = (data, searchTerm = "") => ({
    searchTerm,
    searchId: data.search_id || null,
    searchStatus: normalizeLeadSearchStatus(data.status),
    message: data.message || null,
    pollUrl: data.poll_url || null,
    eventsUrl: data.events_url || null,
    eventsTicketUrl: data.events_ticket_url || null,
    leadResults: getLeadResultsFromResponse(data),
});

const normalizeSearchEventsTicket = (data) => {
    if (typeof data === "string") {
        return { ticket: data };
    }

    return {
        ...(data || {}),
        ticket:
            data?.ticket ||
            data?.events_ticket ||
            data?.access_ticket ||
            null,
    };
};

const getInitialLeadState = () => ({
    leadResults: [],
    searchId: null,
    status: "idle", // ideal, loading, success, failed
    searchStatus: null, // completed, processing
    message: null,
    pollUrl: null,
    error: null,
    lastSearchTerm: "",
    recentActivity: [],
    searchHistory: [],
    activeSearchId: null,
    activeSearchQuery: "",
    searchHistoryStatus: "idle",
    searchHistoryError: null,
});

export const performAiSearch = createAsyncThunk(
    "leads/performAiSearch",
    async (searchTerm, thunkAPI) => {
        try {
            const response = await api.post(API_ENDPOINTS.SEARCH_LEADS, {
                raw_text: searchTerm,
                business_type: "b2b",
            });

            return normalizeSearchResponse(response.data, searchTerm);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch leads."),
            );
        }
    },
);

export const pollSearchResults = createAsyncThunk(
    "leads/pollSearchResults",
    async (pollUrl, thunkAPI) => {
        try {
            const response = await api.get(pollUrl);

            return normalizeSearchResponse(response.data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to poll search results."),
            );
        }
    },
);

export const fetchSearchHistory = createAsyncThunk(
    "leads/fetchSearchHistory",
    async (_, thunkAPI) => {
        try {
            const response = await api.get(API_ENDPOINTS.SEARCH_HISTORY);
            return normalizeSearchHistory(response.data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch search history."),
            );
        }
    },
);

export const createSearchEventsTicket = createAsyncThunk(
    "leads/createSearchEventsTicket",
    async (searchId, thunkAPI) => {
        if (!searchId) {
            return thunkAPI.rejectWithValue("Search ID is required.");
        }

        try {
            const response = await api.post(
                API_ENDPOINTS.SEARCH_EVENTS_TICKET,
                null,
                {
                    params: {
                        search_id: searchId,
                    },
                },
            );

            return normalizeSearchEventsTicket(response.data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(
                    error,
                    "Failed to create search events ticket.",
                ),
            );
        }
    },
);

export const fetchSearchLeadsById = createAsyncThunk(
    "leads/fetchSearchLeadsById",
    async (searchId, thunkAPI) => {
        if (!searchId) {
            return thunkAPI.rejectWithValue("Search ID is required.");
        }

        try {
            const response = await api.get(
                API_ENDPOINTS.SEARCH_LEADS_BY_ID(searchId),
            );

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch search leads."),
            );
        }
    },
);

const leadsSlice = createSlice({
    name: "leads",
    initialState: getInitialLeadState(),
    reducers: {
        clearLeads: (state) => {
            state.leadResults = [];
            state.searchId = null;
            state.searchStatus = null;
            state.status = "idle";
            state.message = null;
            state.pollUrl = null;
            state.error = null;
        },
        setActiveSearch: (state, action) => {
            state.activeSearchId = action.payload?.id || null;
            state.activeSearchQuery = action.payload?.query || "";
        },
        clearSearchHistoryError: (state) => {
            state.searchHistoryError = null;
        },
        updateSearchFromEventSnapshot: (state, action) => {
            const data = action.payload || {};

            state.status = "succeeded";
            state.searchStatus = normalizeLeadSearchStatus(
                data.status || state.searchStatus || "processing",
            );
            state.message = data.message || state.message;
            state.error = null;
        },
        markSearchEventFailed: (state, action) => {
            state.status = "failed";
            state.searchStatus = "failed";
            state.pollUrl = null;
            state.error =
                action.payload ||
                "Lead search failed. Please try again.";
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(performAiSearch.pending, (state) => {
                state.status = "loading";
                state.searchStatus = null;
                state.searchId = null;
                state.pollUrl = null;
                state.message = null;
                state.leadResults = [];
                state.error = null;
                state.activeSearchId = null;
                state.activeSearchQuery = "";
            })
            .addCase(performAiSearch.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.leadResults = action.payload.leadResults;
                state.searchId = action.payload.searchId;
                state.activeSearchId = action.payload.searchId;
                state.activeSearchQuery = action.payload.searchTerm;
                state.searchStatus = action.payload.searchStatus;
                state.message = action.payload.message;
                state.pollUrl =
                    action.payload.searchStatus === "processing" &&
                    !action.payload.eventsUrl
                        ? action.payload.pollUrl
                        : null;
                state.lastSearchTerm = action.payload.searchTerm;

                state.recentActivity = [
                    createRecentActivityItem({
                        type: "search",
                        label: `Searched "${action.payload.searchTerm}"`,
                        query: action.payload.searchTerm,
                    }),
                    ...state.recentActivity,
                ].slice(0, 12);
            })
            .addCase(performAiSearch.rejected, (state, action) => {
                state.status = "failed";
                state.searchStatus = null;
                state.pollUrl = null;
                state.error = action.payload;
            })
            .addCase(pollSearchResults.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.searchId = action.payload.searchId || state.searchId;
                state.activeSearchId =
                    action.payload.searchId || state.activeSearchId;
                state.searchStatus = action.payload.searchStatus;
                state.message = action.payload.message;

                if (action.payload.searchStatus === "completed") {
                    state.leadResults = action.payload.leadResults;
                    state.pollUrl = null;
                } else if (action.payload.searchStatus === "processing") {
                    state.pollUrl = action.payload.pollUrl || state.pollUrl;
                }

                state.error = null;
            })
            .addCase(pollSearchResults.rejected, (state, action) => {
                state.status = "failed";
                state.searchStatus = null;
                state.pollUrl = null;
                state.error = action.payload;
            })
            .addCase(fetchSearchHistory.pending, (state) => {
                state.searchHistoryStatus = "loading";
                state.searchHistoryError = null;
            })
            .addCase(fetchSearchHistory.fulfilled, (state, action) => {
                state.searchHistoryStatus = "succeeded";
                state.searchHistoryError = null;
                state.searchHistory = action.payload;
            })
            .addCase(fetchSearchHistory.rejected, (state, action) => {
                state.searchHistoryStatus = "failed";
                state.searchHistoryError = action.payload;
            })
            .addCase(fetchSearchLeadsById.fulfilled, (state, action) => {
                const data = action.payload;
                const searchId =
                    data?.search_id || action.meta.arg || data?.id || null;

                state.status = "succeeded";
                state.searchStatus = "completed";
                state.searchId = searchId;
                state.activeSearchId = searchId || state.activeSearchId;
                state.leadResults = getLeadResultsFromResponse(data);
                state.pollUrl = null;
                state.message = data?.message || null;
                state.error = null;
            })
            .addCase(logout, () => getInitialLeadState());
    },
});

export const {
    clearLeads,
    setActiveSearch,
    clearSearchHistoryError,
    updateSearchFromEventSnapshot,
    markSearchEventFailed,
} = leadsSlice.actions;

export default leadsSlice.reducer;
